import type {
  LessongroupsItemDto,
  LessongroupsRatingDto,
  LessongroupsResponseDto,
  LessongroupsStudentDto,
  RatingGrade,
  RatingGradeType,
  TeacherMeetingGrouped,
  TeacherMeetingSummary,
  TeacherStudentRating,
  TeacherSubjectGroup,
} from "meetings/advisory/types";
import { toAbsoluteImageUrl } from "shared/utils";

const DEFAULT_MEETING_NAME = "Adviesbespreking";

let nextTempId = -1;

export const findLatestMeetingId = (
  lessonClasses: TeacherSubjectGroup[],
): number | null => {
  let latest: number | null = null;

  for (const lessonClass of lessonClasses) {
    for (const student of lessonClass.students) {
      if (
        student.meetingId !== undefined &&
        (latest === null || student.meetingId > latest)
      ) {
        latest = student.meetingId;
      }
    }
  }
  return latest;
};

const deduplicateStudents = (
  students: LessongroupsStudentDto[],
): LessongroupsStudentDto[] => {
  const seen = new Set<number>();

  return students.filter((s) => {
    if (seen.has(s.id)) {
      return false;
    }

    seen.add(s.id);

    return true;
  });
};

const gradeFromDto = (dto: { type: string; grade: string }): RatingGrade => ({
  type: dto.type as RatingGradeType,
  grade: dto.grade,
});

const studentRatingFromDto = (
  student: LessongroupsStudentDto,
  rating: LessongroupsRatingDto | null,
  fallbackSubject: string,
): TeacherStudentRating => ({
  id: rating?.id ?? nextTempId--,
  studentId: student.id,
  student: {
    id: student.id,
    formattedName:
      student.formatted_name ??
      [student.first_name, student.middle_name, student.last_name]
        .filter(Boolean)
        .join(" "),
    profileImage: toAbsoluteImageUrl(student.profile_image),
    mentorClass: student.mentor_class,
    mentorShortage: student.mentors?.[0]?.shortage,
  },
  rating: rating?.rating ?? null,
  feedback: rating?.feedback ?? null,
  hasHistory: false,
  hasActiveMeeting: rating !== null,
  meetingId: rating?.meeting_id,
  meetingName: rating?.meeting_name,
  subjectName: rating?.subject_name || fallbackSubject,
  teacherShortage: rating?.teacher_shortage,
  grades: (rating?.grades ?? []).map(gradeFromDto),
});

const lessonGroupFromDto = (
  item: LessongroupsItemDto,
): TeacherSubjectGroup => ({
  lessonClass: item.lesson_class,
  subject: item.subject,
  students: deduplicateStudents(item.students).map((student) =>
    studentRatingFromDto(
      student,
      student.ratings.length > 0 ? student.ratings[0] : null,
      item.subject,
    ),
  ),
});

export const lessongroupsFromDto = (
  response: LessongroupsResponseDto,
): TeacherMeetingGrouped | null => {
  const items = response.items ?? [];
  if (items.length === 0) {
    return null;
  }

  const lessonClasses = items.map(lessonGroupFromDto);

  const groupCounts = new Map<string, number>();
  let total = 0;
  for (const lc of lessonClasses) {
    const count = lc.students.length;
    groupCounts.set(
      lc.lessonClass,
      (groupCounts.get(lc.lessonClass) ?? 0) + count,
    );
    total += count;
  }

  const meeting: TeacherMeetingSummary = {
    name: DEFAULT_MEETING_NAME,
    status: "open" as TeacherMeetingSummary["status"],
    message: null,
    progressPercentage: 0,
    opensAt: null,
    closedModifyAt: null,
    closedAt: null,
    ratingOptions: [],
    questionPrompt: null,
    gradeSettings: { enableSeGrade: false, enableRapportGrade: false },
  };

  return {
    meeting,
    lessonClasses,
    groups: [
      { name: "Alle", studentAmount: total },
      ...[...groupCounts.entries()].map(([name, count]) => ({
        name,
        studentAmount: count,
      })),
    ],
    activeClass: null,
    meta: {
      hasMore: false,
      offset: 0,
      take: total,
      total,
    },
  };
};
